// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * WebSocket Registry for PWA Background Sync
 * Allows the sync module to send messages through active WebSocket connections
 */

import { logger } from './logger';
import type { SendMessageFn } from '../types/messages';

interface ConnectionInfo {
  sessionId: string;
  playerId: string;
  sendMessage: SendMessageFn;
}

class WebSocketRegistry {
  private connections = new Map<string, ConnectionInfo>();

  /**
   * Register a WebSocket connection for a session
   */
  register(sessionId: string, playerId: string, sendMessage: SendMessageFn): void {
    this.connections.set(sessionId, { sessionId, playerId, sendMessage });
    logger.debug('WebSocket registered', { sessionId, playerId });
  }

  /**
   * Unregister a WebSocket connection
   */
  unregister(sessionId: string): void {
    this.connections.delete(sessionId);
    logger.debug('WebSocket unregistered', { sessionId });
  }

  /**
   * Send a message through a registered WebSocket
   */
  sendMessage(sessionId: string, action: string, payload: Record<string, unknown>): boolean {
    const connection = this.connections.get(sessionId);

    if (!connection) {
      logger.warn('No WebSocket connection for session', { sessionId });
      return false;
    }

    try {
      connection.sendMessage(action, payload);
      logger.debug('Message sent via registry', { sessionId, action });
      return true;
    } catch (error) {
      logger.error('Failed to send message via registry', {
        sessionId,
        action,
        error,
      });
      return false;
    }
  }

  /**
   * Check if a session has an active WebSocket connection
   */
  hasConnection(sessionId: string): boolean {
    return this.connections.has(sessionId);
  }

  /**
   * Get all active session IDs
   */
  getActiveSessions(): string[] {
    return Array.from(this.connections.keys());
  }

  /**
   * Get connection info for a session
   */
  getConnection(sessionId: string): ConnectionInfo | undefined {
    return this.connections.get(sessionId);
  }

  /**
   * Clear all connections
   */
  clear(): void {
    this.connections.clear();
    logger.info('WebSocket registry cleared');
  }
}

// Export singleton instance
export const wsRegistry = new WebSocketRegistry();

// Export class for testing
export { WebSocketRegistry };
